import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';

import InfoButton from './InfoButton';

import { InputField, Button, Form } from '@/components';
import useTapTourney from '@/hooks/useTapTourney';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { TapRegistrationService } from '@/services/tda';
import { TAPorPremioTO, tourneyModalState } from '@/types/tda';
import {
  enterPinTeam11,
  formatNumberToLocale,
  validateFieldError,
} from '@/utils';

type PinEntryFormProps = {
  item: TAPorPremioTO;
  pinSugerido?: string;
  handleTourneyModal: (updates: Partial<tourneyModalState>) => void;
};

const PinEntryForm = ({
  item,
  pinSugerido,
  handleTourneyModal,
}: PinEntryFormProps) => {
  const { tapTourneyData, handleSetTapTourney } = useTapTourney();

  const handleIsOpenInfo = (boolean: boolean) => {
    handleTourneyModal({
      open: false,
    });
    handleSetTapTourney({
      ...tapTourneyData,
      isOpenInfo: boolean,
    });
  };

  const navigate = useNavigate();
  const toast = useToast();
  const {
    touched,
    isValidating,
    isValid,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    values,
    handleBlur,
  } = useFormik({
    initialValues: {
      pin: pinSugerido || '',
    },
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: enterPinTeam11,
    onSubmit: async (val, { setSubmitting }) => {
      if (val.pin) {
        const res = await TapRegistrationService(item.id, val.pin);
        if (res.ok) {
          handleTourneyModal({
            open: false,
            title: '',
            content: null,
            openSuccessModal: true,
          });
        } else {
          if (res.codError === 3303) {
            toast.show(
              `No existe cupo para el Torneo de Amigos por Premios seleccionado, en unos minutos podrás participar en otro Torneo de Amigos por ${formatNumberToLocale(
                item.premio ?? 0
              )}`
            );
            handleTourneyModal({
              open: false,
            });
          } else if (res.codError === 3300) {
            toast.show('La inscripción libre ya fue utilizada en este torneo.');
            handleTourneyModal({
              open: false,
            });
          } else if (res.codError === 3301) {
            toast.show(res.mensaje);
          } else {
            toast.show(res.mensaje);
            handleTourneyModal({
              open: false,
            });
          }
        }
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="flex flex-col items-center content-center mt-4">
      <h1 className="font-semibold text-center">
        {`Necesitás un Pin Especial de $${
          item.tipoProducto.precioDeLista ?? 0
        } para participar en el Torneo de Amigos por Premios.`}
      </h1>
      <div className=" items-center content-center space-x-4 mt-4 w-full">
        <Form.Form loading={isSubmitting} onSubmit={handleSubmit}>
          <Form.Field>
            <InputField
              autoComplete="off"
              behavior="default"
              error={validateFieldError(
                touched.pin,
                errors.pin,
                values.pin.length
              )}
              maxLength={12}
              name="pin"
              placeholder="Pin"
              success={!errors.pin && values.pin.length > 0}
              type="text"
              value={values.pin}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Form.Field>
          <div className="flex flex-col items-center mt-4">
            <Button
              className="w-full"
              disabled={!isValid || isValidating || !values.pin || isSubmitting}
              type="submit"
              variant="primary"
            >
              Confirmar
            </Button>
            <Button
              className="mt-4 font-bold"
              type="button"
              variant="text"
              onClick={() =>
                navigate(ROUTES.myAccount, {
                  state: { scrollToEspecial: true },
                })
              }
            >
              Obtener pin especial
            </Button>

            <InfoButton
              handleIsOpenInfo={handleIsOpenInfo}
              hideButton={false}
            />
          </div>
        </Form.Form>
      </div>
    </div>
  );
};

export default PinEntryForm;
